import { api } from '@/lib/api/client';
import { getAdminDemoActor } from '@/pages/admin/demo/role/adminDemoRole';

export interface Withdrawal {
  id: string;
  amount: number;
  status: string;
  requestedAt: string;
  paidAt: string | null;
}

export interface AffiliateView {
  joined: boolean;
  code: string | null;
  clicks: number;
  signups: number;
  earned: number;
  paid: number;
  available: number;
  pending: number;
  commissionPct: number;
  minWithdrawal: number;
  withdrawals: Withdrawal[];
}

const DEMO_AFF_KEY = 'maktech_demo_affiliate_view';

function isDemoSession(): boolean {
  const actor = getAdminDemoActor();
  return Boolean(actor?.email);
}

function defaultDemoAffiliate(): AffiliateView {
  return {
    joined: false,
    code: null,
    clicks: 0,
    signups: 0,
    earned: 0,
    paid: 0,
    available: 0,
    pending: 0,
    commissionPct: 12,
    minWithdrawal: 300000,
    withdrawals: [],
  };
}

function loadDemoAffiliate(): AffiliateView {
  if (typeof window === 'undefined') return defaultDemoAffiliate();
  const actor = getAdminDemoActor();
  const key = `${DEMO_AFF_KEY}:${actor.email}`;
  const raw = window.localStorage.getItem(key);
  if (!raw) return defaultDemoAffiliate();
  try {
    return { ...defaultDemoAffiliate(), ...(JSON.parse(raw) as Partial<AffiliateView>) };
  } catch {
    return defaultDemoAffiliate();
  }
}

function saveDemoAffiliate(view: AffiliateView): void {
  if (typeof window === 'undefined') return;
  const actor = getAdminDemoActor();
  const key = `${DEMO_AFF_KEY}:${actor.email}`;
  window.localStorage.setItem(key, JSON.stringify(view));
}

function demoAffiliateCode(email: string): string {
  const base = email.split('@')[0]?.replace(/[^a-z0-9]/gi, '') || 'USER';
  return `MAK-${base.slice(0, 8).toUpperCase()}`;
}

export async function getAffiliate(): Promise<AffiliateView> {
  try {
    return await api.get<AffiliateView>('/affiliate/me').then((r) => r.data);
  } catch (e) {
    if (!isDemoSession()) throw e;
    return loadDemoAffiliate();
  }
}

export async function joinAffiliate(): Promise<AffiliateView> {
  try {
    return await api.post<AffiliateView>('/affiliate/join').then((r) => r.data);
  } catch (e) {
    if (!isDemoSession()) throw e;
    const actor = getAdminDemoActor();
    const current = loadDemoAffiliate();
    const next: AffiliateView = {
      ...current,
      joined: true,
      code: current.code ?? demoAffiliateCode(actor.email),
    };
    saveDemoAffiliate(next);
    return next;
  }
}

export async function withdrawAffiliate(): Promise<{ affiliate: AffiliateView }> {
  try {
    return await api.post<{ affiliate: AffiliateView }>('/affiliate/withdraw').then((r) => r.data);
  } catch (e) {
    if (!isDemoSession()) throw e;
    const current = loadDemoAffiliate();
    if (!current.joined) throw new Error('Join the affiliate programme first.');
    if (current.available < current.minWithdrawal) {
      throw new Error('Minimum withdrawal not reached yet.');
    }
    const amount = current.available;
    const next: AffiliateView = {
      ...current,
      available: 0,
      pending: current.pending + amount,
      withdrawals: [
        {
          id: `wd_${Date.now()}`,
          amount,
          status: 'pending',
          requestedAt: new Date().toISOString(),
          paidAt: null,
        },
        ...current.withdrawals,
      ],
    };
    saveDemoAffiliate(next);
    return { affiliate: next };
  }
}

export function submitTeachLead(payload: { name: string; email: string; topic: string }): Promise<unknown> {
  return api.post('/leads/teach', payload).then((r) => r.data);
}

export function submitBusinessLead(payload: {
  name: string;
  company: string;
  email: string;
  teamSize: string;
}): Promise<unknown> {
  return api.post('/leads/business', payload).then((r) => r.data);
}

export function submitPdfLead(payload: {
  name: string;
  email: string;
  phone?: string;
  wanted: string;
  source: string;
}): Promise<unknown> {
  return api.post('/leads', payload).then((r) => r.data);
}

export function submitContactForm(payload: {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}): Promise<unknown> {
  return api
    .post('/leads', {
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      wanted: `[${payload.subject}] ${payload.message}`,
      source: 'Contact page',
    })
    .then((r) => r.data);
}

export function submitAffiliateApplication(payload: {
  name: string;
  email: string;
  phone: string;
}): Promise<unknown> {
  return api
    .post('/leads', {
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      wanted: 'Affiliate programme registration',
      source: 'Affiliate landing',
    })
    .then((r) => r.data);
}

export function bookCounselling(payload: {
  name: string;
  phone: string;
  email?: string;
  goal: string;
  slot: string;
}): Promise<unknown> {
  return api.post('/counselling', payload).then((r) => r.data);
}

export function bookCentreClass(
  centreSlug: string,
  payload: { name: string; phone: string; email?: string; slot: string },
): Promise<unknown> {
  return api.post(`/centres/${centreSlug}/book`, payload).then((r) => r.data);
}
